import store from '@/store/index.js'
import { sasNeedsRefresh, createResourceUrl, blobSegmentsFromUrl } from '@/utils/SasHelpers'
import ApiServer from '@/utils/api/ApiServer'
import { BlobServiceClient } from '@azure/storage-blob'
import i18n from '@/i18n'
import { SasTokenLocations } from '@/utils/LocalStorageActor'
import { ATTACHMENT_MIMES, MAX_ATTACHMENT_FILES, uniqueFileName, validateAttachment } from '@/utils/Files'
import { isGpxFile } from '@/utils/map/Gpx'

export const VISIT_FILE_ACCEPT = 'image/png,image/jpeg,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.gpx,application/gpx+xml'

export const validateVisitFile = (file) =>
  validateAttachment(file, f => ATTACHMENT_MIMES.has(f.type) || f.type === 'application/gpx+xml' || isGpxFile(f.name))

class TractVisitFilesClient {
  async getContainerClient () {
    await this.initConnection()
    return this.containerClient
  }

  getEndpoint () {
    return 'companyinfo/blobinfo/tractvisitfiles'
  }

  async initConnection () {
    const sasCapsule = JSON.parse(localStorage.getItem(SasTokenLocations.TRACT_VISIT_FILES_SAS))
    if (sasCapsule === null || sasNeedsRefresh(sasCapsule.data[0].blobSasUri)) {
      const response = await ApiServer.get(ApiServer.urlFor(this.getEndpoint()))
      localStorage.setItem(SasTokenLocations.TRACT_VISIT_FILES_SAS, JSON.stringify(response))
    }

    const storedResponse = JSON.parse(localStorage.getItem(SasTokenLocations.TRACT_VISIT_FILES_SAS))
    this.container = storedResponse.data[0].container
    this.location = storedResponse.data[0].location
    this.blobSasUri = storedResponse.data[0].blobSasUri
    this.client = new BlobServiceClient(this.blobSasUri)
    this.containerClient = this.client.getContainerClient(this.container)
  }

  getBlobLocation (tractVisitId) {
    return `${this.location}/${tractVisitId}/`
  }

  async getFiles (tractVisitId) {
    if (!tractVisitId) return []
    const files = []
    try {
      const containerClient = await this.getContainerClient()
      const iterator = containerClient.listBlobsFlat({ prefix: this.getBlobLocation(tractVisitId) }).byPage()
      const response = (await iterator.next()).value
      for (const blob of response.segment.blobItems) {
        const resourceUrl = createResourceUrl(this.blobSasUri, this.container, blob.name)
        const name = decodeURIComponent(resourceUrl.pathname.split('/').pop())
        const contentType = blob.properties.contentType ?? ''
        files.push({
          name,
          type: name.split('.').pop(),
          contentType,
          isImage: contentType.startsWith('image/'),
          isGpx: isGpxFile(name),
          date: blob.properties.lastModified,
          ref: resourceUrl.href
        })
      }
    } catch (e) {
      console.error(e)
      return []
    }
    return files
  }

  async deleteFile (tractId, fileUrl) {
    try {
      await this.initConnection()
      const [tractVisitId, ...fileName] = blobSegmentsFromUrl(fileUrl, this.location)
      await ApiServer.delete(ApiServer.urlFor(`tracts/${tractId}/visits/${tractVisitId}/files/${fileName.map(encodeURIComponent).join('/')}`))
    } catch (e) {
      console.error(e)
    }
  }

  async deleteAllFiles (tractId, tractVisitId) {
    const files = await this.getFiles(tractVisitId)
    for (const file of files) {
      await this.deleteFile(tractId, file.ref)
    }
  }

  async uploadFile (visit, visitFile) {
    const files = await this.getFiles(visit.tractVisitId)
    if (files.length >= MAX_ATTACHMENT_FILES) return false

    const storedFileName = uniqueFileName(visitFile.name, files.map(f => f.name))
    const contentType = isGpxFile(visitFile.name) ? 'application/gpx+xml' : visitFile.type
    const fileForUpload = new File([visitFile], storedFileName, { type: contentType })

    try {
      const containerClient = await this.getContainerClient()
      const blockBlobClient = containerClient.getBlockBlobClient(this.getBlobLocation(visit.tractVisitId) + fileForUpload.name)
      await blockBlobClient.uploadData(fileForUpload, {
        blobHTTPHeaders: {
          blobContentType: fileForUpload.type
        }
      })
      return true
    } catch (e) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorUploadingVisitFile'))
      console.error(e)
      return false
    }
  }
}

export const tractVisitFilesClient = new TractVisitFilesClient()
